import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Stack,
  Switch,
  TabPanel,
  TabPanels,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { disabledInputStyles } from '@diamond/shared/ui';
import { PillTabs } from '@diamond/sol-admin/components';
import { useEffect, useMemo, useRef } from 'react';
import {
  type UseFormReturn,
  Controller,
  FieldError,
  useFieldArray,
} from 'react-hook-form';

import {
  REWARD_DESCRIPTIONS,
  VOUCHER_REWARD_TYPE_OPTIONS,
} from '../../constants';
import {
  useAutoAssignDiscountProduct,
  useAutoAssignFreeProduct,
  useEnsurePurchaseProductField,
  useInitializeRewardConfiguration,
  useRewardTypeChangeHandler,
  useSyncRewardProducts,
} from '../../hooks';
import type { CreateVoucherRequest } from '../../types';
import type {
  PurchaseProductOption,
  RewardType,
} from '../../types/voucher-product-form.types';
import { getFieldErrorMessage, getRewardTypeIndex } from '../../utils';
import { PurchaseProductsFieldset } from './purchase-products-fieldset';
import {
  AmountRewardFields,
  FreeGoodsRewardFields,
  PercentRewardFields,
} from './reward-fields';

interface VoucherProductFormProps {
  form: UseFormReturn<CreateVoucherRequest>;
  disabled?: boolean;
}

export function VoucherProductForm({
  form,
  disabled = false,
}: VoucherProductFormProps) {
  const {
    control,
    register,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = form;

  const rewardConfiguration = watch('reward_configuration');
  const rewardDiscountProduct = watch('rewardDiscountProduct');
  const rewardFreeProduct = watch('rewardFreeProduct');

  const rewardType: RewardType =
    rewardConfiguration?.reward_type ?? 'DISCOUNT_PERCENT';

  const {
    fields: purchaseProductFields,
    append,
    remove,
  } = useFieldArray({ control, name: 'purchaseProducts' });

  // Derive purchaseProducts from useFieldArray fields for reliable state tracking
  const purchaseProducts = useMemo(
    () =>
      purchaseProductFields.map((field) => ({
        value: field.value,
        label: field.label,
        division: field.division,
      })) as PurchaseProductOption[],
    [purchaseProductFields]
  );

  // Track previous reward type to detect changes
  const prevRewardTypeRef = useRef(rewardType);

  useInitializeRewardConfiguration(rewardConfiguration, setValue);
  useEnsurePurchaseProductField(append, purchaseProductFields.length > 0);
  useAutoAssignDiscountProduct({
    rewardType,
    purchaseProducts,
    currentValue: rewardDiscountProduct,
    setValue,
    clearErrors,
    manuallyCleared: false, // Always false - discount field is permanently disabled
  });
  useAutoAssignFreeProduct({
    rewardType,
    purchaseProducts,
    currentValue: rewardFreeProduct,
    setValue,
    clearErrors,
    manuallyCleared: false, // Always false - FREE_GOODS field is permanently disabled
  });

  // Hook to manually sync reward products when purchase products change
  const { syncRewardProducts } = useSyncRewardProducts({
    rewardType,
    setValue,
    clearErrors,
    manuallyClearedDiscountProduct: false, // Always false - discount field is permanently disabled
    manuallyClearedFreeProduct: false, // Always false - FREE_GOODS field is permanently disabled
  });

  // Enforce FREE_GOODS quantities to 1 (including when loaded from draft)
  useEffect(() => {
    if (rewardType === 'FREE_GOODS') {
      const currentMinQty = rewardConfiguration?.min_total_quantity_all_item;
      const currentFreeQty = rewardConfiguration?.free_goods_quantity_value;

      if (currentMinQty !== 1) {
        setValue('reward_configuration.min_total_quantity_all_item', 1, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      if (currentFreeQty !== 1) {
        setValue('reward_configuration.free_goods_quantity_value', 1, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [rewardType, rewardConfiguration, setValue]);

  // Centralized auto-assignment of reward products
  // Handles both: reward type changes AND purchase product changes
  // All reward product fields are permanently disabled (Buy A Get A logic)
  useEffect(() => {
    prevRewardTypeRef.current = rewardType;

    // Get valid purchase products
    const validPurchaseProducts = purchaseProducts.filter(
      (product) => product?.value && product.value !== ''
    );

    if (validPurchaseProducts.length === 0) return;

    const firstProduct = validPurchaseProducts[0];
    const isDiscountReward =
      rewardType === 'DISCOUNT_AMOUNT' || rewardType === 'DISCOUNT_PERCENT';

    // Auto-assign based on current reward type (always auto-assign since fields are disabled)
    if (isDiscountReward) {
      if (!rewardDiscountProduct?.value) {
        setValue('rewardDiscountProduct', firstProduct, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        clearErrors('rewardDiscountProduct');
      }
    } else if (rewardType === 'FREE_GOODS') {
      if (!rewardFreeProduct?.value) {
        setValue('rewardFreeProduct', firstProduct, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        clearErrors('rewardFreeProduct');
      }
    }
  }, [
    rewardType,
    purchaseProducts,
    rewardDiscountProduct,
    rewardFreeProduct,
    setValue,
    clearErrors,
  ]);

  /**
   * Builds an updated purchase products list by replacing the appropriate slot
   * with the new product. Replaces either the last item or the first empty slot.
   */
  const buildUpdatedPurchaseProducts = (
    newProduct: PurchaseProductOption
  ): PurchaseProductOption[] => {
    const emptySlotIndex = purchaseProducts.findIndex((pp) => pp.value === '');
    const targetIndex =
      emptySlotIndex !== -1 ? emptySlotIndex : purchaseProducts.length - 1;

    return purchaseProducts.map((product, idx) => {
      if (idx === targetIndex) {
        return newProduct;
      }
      return product;
    });
  };

  // Callback to handle purchase product changes and sync reward products
  // Pass the new product directly to avoid stale closure issue
  const handlePurchaseProductChange = (
    newProduct?: PurchaseProductOption | null
  ) => {
    if (!newProduct) {
      syncRewardProducts(
        purchaseProducts,
        rewardDiscountProduct,
        rewardFreeProduct
      );
      return;
    }

    const updatedPurchaseProducts = buildUpdatedPurchaseProducts(newProduct);
    syncRewardProducts(
      updatedPurchaseProducts,
      rewardDiscountProduct,
      rewardFreeProduct
    );
  };

  const rewardTypeIndex = getRewardTypeIndex(rewardType);
  const rewardDescription =
    REWARD_DESCRIPTIONS[rewardType as keyof typeof REWARD_DESCRIPTIONS] ?? '';

  const purchaseProductErrorMessage =
    typeof errors.purchaseProducts?.message === 'string'
      ? errors.purchaseProducts.message
      : undefined;

  const purchaseProductFieldErrors = Array.isArray(errors.purchaseProducts)
    ? (errors.purchaseProducts as (
        | FieldError
        | { value?: FieldError }
        | undefined
      )[])
    : undefined;

  const handleRewardTypeChange = useRewardTypeChangeHandler({
    setValue,
    clearErrors,
  });

  // Convert purchaseProducts to SelectOption[] for reward product picker
  const purchaseProductOptions: SelectOption[] = useMemo(
    () =>
      purchaseProducts
        .filter((product) => product?.value && product.value !== '')
        .map((product) => ({
          value: product.value,
          label: product.label,
        })),
    [purchaseProducts]
  );

  return (
    <VStack spacing={8} align="stretch">
      <Text fontSize="lg" fontWeight="semibold" color="gray.800">
        Ketentuan Pembelian Produk
      </Text>

      <PurchaseProductsFieldset
        control={control}
        fields={purchaseProductFields}
        append={append}
        remove={remove}
        fieldErrors={purchaseProductFieldErrors}
        errorMessage={purchaseProductErrorMessage}
        disabled={disabled}
        onProductChange={handlePurchaseProductChange}
      />

      <FormControl
        isInvalid={!!errors.reward_configuration?.min_total_quantity_all_item}
        display="flex"
        w="full"
        gap={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
          Minimal kuantitas pembelian produk
        </FormLabel>
        <Input
          type="number"
          min={1}
          fontSize="sm"
          maxW={{ base: 'full', sm: '120px' }}
          textAlign="center"
          borderRadius="lg"
          borderColor="gray.200"
          fontWeight="medium"
          focusBorderColor="blue.500"
          _focus={{
            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
          }}
          {...register('reward_configuration.min_total_quantity_all_item', {
            valueAsNumber: true,
          })}
          isDisabled={disabled || rewardType === 'FREE_GOODS'}
          sx={disabledInputStyles}
        />
        <FormErrorMessage>
          {
            errors.reward_configuration?.min_total_quantity_all_item
              ?.message as string
          }
        </FormErrorMessage>
      </FormControl>

      <FormControl>
        <Stack
          direction={{ base: 'column', md: 'row' }}
          spacing={4}
          align="center"
        >
          <Box flex={1}>
            <FormLabel
              fontSize="sm"
              fontWeight="semibold"
              color="gray.700"
              mb={1}
            >
              Bundling / Mix variant
            </FormLabel>
            <Text fontSize="xs" color="gray.500">
              Izinkan kombinasi produk berbeda dalam satu bundel
            </Text>
          </Box>
          <Controller
            control={control}
            name="reward_configuration.is_bundling"
            render={({ field }) => (
              <HStack spacing={3} align="center">
                <Text
                  fontSize="sm"
                  color={!field.value ? 'gray.700' : 'gray.400'}
                >
                  Tidak
                </Text>
                <Switch
                  size="lg"
                  isChecked={Boolean(field.value)}
                  onChange={(event) => field.onChange(event.target.checked)}
                  colorScheme="linkedin"
                  isDisabled={disabled}
                />
                <Text
                  fontSize="sm"
                  color={field.value ? 'gray.700' : 'gray.400'}
                >
                  Ya
                </Text>
              </HStack>
            )}
          />
        </Stack>
      </FormControl>

      <FormControl isInvalid={!!errors.reward_configuration?.reward_type}>
        <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
          Jenis Reward
        </FormLabel>
        <PillTabs
          tablist={VOUCHER_REWARD_TYPE_OPTIONS.map(
            (option: { label: string; value: string }) => ({
              name: option.label,
            })
          )}
          index={rewardTypeIndex}
          onChange={disabled ? () => undefined : handleRewardTypeChange}
          isDisabled={disabled}
          disabledIndices={[0]} // Disable DISCOUNT_AMOUNT (index 0)
        >
          <TabPanels display="none">
            {VOUCHER_REWARD_TYPE_OPTIONS.map(
              (option: { label: string; value: string }) => (
                <TabPanel key={option.value} p={0} />
              )
            )}
          </TabPanels>
        </PillTabs>
        {rewardDescription ? (
          <Text fontSize="xs" color="gray.500" mt={3}>
            {rewardDescription}
          </Text>
        ) : null}
        <FormErrorMessage>
          {errors.reward_configuration?.reward_type?.message as string}
        </FormErrorMessage>
      </FormControl>

      <RewardFieldsRenderer
        rewardType={rewardType}
        form={form}
        errors={errors}
        purchaseProductOptions={purchaseProductOptions}
      />
    </VStack>
  );
}

interface RewardFieldsRendererProps {
  rewardType: RewardType;
  form: UseFormReturn<CreateVoucherRequest>;
  errors: UseFormReturn<CreateVoucherRequest>['formState']['errors'];
  purchaseProductOptions: SelectOption[];
}

function RewardFieldsRenderer({
  rewardType,
  form,
  errors,
  purchaseProductOptions,
}: RewardFieldsRendererProps) {
  const rewardFieldsMap = {
    DISCOUNT_AMOUNT: (
      <AmountRewardFields
        form={form}
        purchaseProductOptions={purchaseProductOptions}
        errorMessage={getFieldErrorMessage(
          errors.reward_configuration?.discount_amount_value
        )}
        productError={getFieldErrorMessage(errors.rewardDiscountProduct)}
      />
    ),
    DISCOUNT_PERCENT: (
      <PercentRewardFields
        form={form}
        purchaseProductOptions={purchaseProductOptions}
        errorMessage={getFieldErrorMessage(
          errors.reward_configuration?.discount_percentage_value
        )}
        productError={getFieldErrorMessage(errors.rewardDiscountProduct)}
      />
    ),
    FREE_GOODS: (
      <FreeGoodsRewardFields
        form={form}
        purchaseProductOptions={purchaseProductOptions}
        quantityError={getFieldErrorMessage(
          errors.reward_configuration?.free_goods_quantity_value
        )}
        productError={getFieldErrorMessage(errors.rewardFreeProduct)}
      />
    ),
  } as const;

  return rewardFieldsMap[rewardType] ?? null;
}
